import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Space, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import Api from '../../Network';
import { InfiniteSelect } from '../../Component/InfiniteSelect';
import NameByLang from '../../Component/nameByLang';
// Styles imported from parent

const { TextArea } = Input;
const { Option } = Select;

interface PermissionModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  permission: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const PermissionModal: React.FC<PermissionModalProps> = ({
  visible,
  mode,
  permission,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Get initial model options for the InfiniteSelect when editing
  const initialModelOptions = useMemo(() => {
    if (mode === 'edit' && permission) {
      const model = permission.menu_models?.[0] || permission.menuModels?.[0];
      if (model) {
        return [model];
      }
    }
    return [];
  }, [mode, permission]);

  // Create permission mutation
  const createPermissionMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/permissions', data),
    onSuccess: () => {
      message.success(t('permissions.addSuccess', 'Permission created successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.msg || t('common.error', 'Error creating permission');
        message.error(errorMessage);
      }
    },
  });

  // Update permission mutation
  const updatePermissionMutation = useMutation({
    mutationFn: (data: any) => Api.put(`/api/admin/permissions/${permission?.id}`, data),
    onSuccess: () => {
      message.success(t('permissions.updateSuccess', 'Permission updated successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.msg || t('common.error', 'Error updating permission');
        message.error(errorMessage);
      }
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // With singleMode=true, model_id is returned directly as the ID
      const submitValues = { ...values };

      if (mode === 'create') {
        await createPermissionMutation.mutateAsync(submitValues);
      } else if (mode === 'edit') {
        await updatePermissionMutation.mutateAsync(submitValues);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Set form values when editing
  useEffect(() => {
    if (visible && permission && mode === 'edit') {
      // Get the model this permission belongs to (if any)
      const model = permission.menu_models?.[0] || permission.menuModels?.[0];
      form.setFieldsValue({
        name: permission.name,
        display_name_en: permission.display_name_en,
        display_name_ar: permission.display_name_ar,
        description_en: permission.description_en,
        description_ar: permission.description_ar,
        category: permission.category || undefined,
        // With singleMode=true, just set the ID directly
        model_id: model ? model.id : undefined,
      });
    } else if (visible && mode === 'create') {
      form.resetFields();
    }
  }, [visible, permission, mode, form]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return t('permissions.addNew', 'Add New Permission');
      case 'edit':
        return t('permissions.edit', 'Edit Permission');
      default:
        return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="name"
          label={t('permissions.form.name', 'Name')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input
            placeholder={t(
              'permissions.form.namePlaceholder',
              'Enter permission name (e.g., orders.create)'
            )}
          />
        </Form.Item>

        <Form.Item
          name="display_name_en"
          label={t('permissions.form.displayNameEn', 'Display Name (English)')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input
            placeholder={t(
              'permissions.form.displayNameEnPlaceholder',
              'Enter display name in English'
            )}
          />
        </Form.Item>

        <Form.Item
          name="display_name_ar"
          label={t('permissions.form.displayNameAr', 'Display Name (Arabic)')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input
            placeholder={t(
              'permissions.form.displayNameArPlaceholder',
              'Enter display name in Arabic'
            )}
          />
        </Form.Item>

        <Form.Item
          name="description_en"
          label={t('permissions.form.descriptionEn', 'Description (English)')}
        >
          <TextArea
            rows={4}
            placeholder={t(
              'permissions.form.descriptionEnPlaceholder',
              'Enter description in English'
            )}
          />
        </Form.Item>

        <Form.Item
          name="description_ar"
          label={t('permissions.form.descriptionAr', 'Description (Arabic)')}
        >
          <TextArea
            rows={4}
            placeholder={t(
              'permissions.form.descriptionArPlaceholder',
              'Enter description in Arabic'
            )}
          />
        </Form.Item>

        <Form.Item
          name="model_id"
          label={t('permissions.form.model', 'Assign to Model (Optional)')}
        >
          <InfiniteSelect
            queryKey="/api/admin/models"
            placeholder={t('permissions.form.selectModel', 'Select a model (optional)')}
            style={{ width: '100%' }}
            allowClear
            singleMode={true}
            initialOptions={initialModelOptions}
            labelRender={(option: any) => {
              const item = 'item' in option ? option.item : option;
              return (
                <NameByLang
                  name_en={item?.name_en}
                  name_ar={item?.name_ar}
                  name={item?.name || item?.key}
                />
              );
            }}
            valueField="id"
            searchField="name"
          />
        </Form.Item>

        <Form.Item
          name="category"
          label={t('permissions.form.category', 'Column Position (Optional)')}
        >
          <Select
            placeholder={t('permissions.form.selectCategory', 'Select column position')}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="view_all">{t('permissions.columns.viewAll', 'View All')}</Option>
            <Option value="view_details">
              {t('permissions.columns.viewDetails', 'View Details')}
            </Option>
            <Option value="create">{t('permissions.columns.create', 'Create')}</Option>
            <Option value="update">{t('permissions.columns.update', 'Update')}</Option>
            <Option value="delete">{t('permissions.columns.delete', 'Delete')}</Option>
            <Option value="other">{t('permissions.columns.other', 'Other')}</Option>
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === 'create' ? t('common.add', 'Add') : t('common.update', 'Update')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PermissionModal;
